import { HARDCODED_COMPANY_SOURCES, MM_FALLBACK_COMPANY_SOURCE_ID } from './companySourceConfig';

const ALL_COMPANY_SOURCE_IDS = HARDCODED_COMPANY_SOURCES.map((source) => source.id);

/**
 * @param {Object|null|undefined} itemMetadata
 * @returns {number[]|null} null when companySources field is missing (unknown eligibility)
 */
function extractCompanySourceIdsFromItemMetadata(itemMetadata) {
    const raw = itemMetadata?.companySources;
    if (!Array.isArray(raw)) return null;
    return raw
        .map((entry) => Number(entry?.companySourceId ?? entry?.companySourceID))
        .filter((id) => Number.isFinite(id));
}

/**
 * Returns company source ids eligible for a PO line item from item metadata.
 * No API call — reads companySources array on the catalog/model row.
 *
 * @param {Object|null|undefined} itemMetadata
 * @returns {number[]}
 */
export function getEligibleCompanySourceIdsForLineItem(itemMetadata) {
    const extracted = extractCompanySourceIdsFromItemMetadata(itemMetadata);
    if (extracted === null) {
        return ALL_COMPANY_SOURCE_IDS;
    }
    if (extracted.length === 0) {
        return [];
    }
    const allowed = new Set(extracted);
    return ALL_COMPANY_SOURCE_IDS.filter((id) => allowed.has(id));
}

/**
 * Returns company source ids to pre-select when a catalog item is chosen.
 * - Misfits Market when it is among eligible company sources
 * - Otherwise the sole company source when only one is eligible
 * - Otherwise none (multiple company sources, none of which is MM)
 *
 * @param {Object|null|undefined} itemMetadata
 * @returns {number[]}
 */
export function getDefaultCompanySourceIdsForLineItem(itemMetadata) {
    if (!itemMetadata) return [];
    const eligible = getEligibleCompanySourceIdsForLineItem(itemMetadata);
    if (eligible.length === 0) return [];
    if (eligible.includes(MM_FALLBACK_COMPANY_SOURCE_ID)) return [MM_FALLBACK_COMPANY_SOURCE_ID];
    if (eligible.length === 1) return [eligible[0]];
    return [];
}

/**
 * True when item metadata explicitly lists companySources as an empty array (invalid eligibility).
 *
 * @param {Object|null|undefined} itemMetadata
 * @returns {boolean}
 */
export function hasInvalidEmptyCompanySourceEligibility(itemMetadata) {
    const extracted = extractCompanySourceIdsFromItemMetadata(itemMetadata);
    return extracted !== null && extracted.length === 0;
}

/**
 * Keeps only company source ids eligible for the item. When none remain, returns defaults for the item.
 *
 * @param {Array<number|string>} selectedCompanySourceIds
 * @param {Object|null|undefined} itemMetadata
 * @returns {number[]}
 */
export function reconcileSelectedCompanySourceIdsForLineItem(selectedCompanySourceIds, itemMetadata) {
    const eligible = getEligibleCompanySourceIdsForLineItem(itemMetadata);
    const eligibleSet = new Set(eligible);
    const reconciled = (Array.isArray(selectedCompanySourceIds) ? selectedCompanySourceIds : [])
        .map((id) => Number(id))
        .filter((id) => eligibleSet.has(id));
    if (reconciled.length > 0) return reconciled;
    return getDefaultCompanySourceIdsForLineItem(itemMetadata);
}
